import { Dialog, DialogTitle, DialogContent, Button, DialogActions, CircularProgress } from '@mui/material';
import Draggable from 'react-draggable';
import React, { useState, useRef } from 'react';
import { withdrawOffer, handleUnauthorizedError } from '../Services/OfferService';
import OfferGridData from '../Models/OfferGridData';
import SnackbarMessage from "./SnackbarMessage";
import Paper, { PaperProps } from '@mui/material/Paper';
import OfferSummary from './OfferSummary';

type WithdrawOfferPopUpProps = {
  open: boolean;
  onClose: (offerWithdraw: boolean) => void;
  offer: OfferGridData;
  tonQuotaTypes: number[];
}

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#withdraw-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

export default function WithdrawOfferPopUp({ open, onClose, offer, tonQuotaTypes: tonQuotaTypes }: WithdrawOfferPopUpProps) {

  const [snackbarData, setSnackbarData] = React.useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);

  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const isProcessingRef = useRef(false);

  const handleClose = () => {
    if (isProcessingRef.current) return;
    onClose(false);
  };

  const closeSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleWithdraw = async () => {
      if (isProcessingRef.current) return;
  
      isProcessingRef.current = true;
      setIsWithdrawing(true);
  
      try {
          await withdrawOffer(offer.id);
          onClose(true);
      } catch (error) {
          handleUnauthorizedError(error);
          setSnackbarOpen(true);
          setSnackbarData({ message: 'Ekki tókst að afturkalla tilboð. Vinsamlegast reynið aftur síðar.', type: 'error' });
          console.error(error);
      } finally {
          setIsWithdrawing(false);
          isProcessingRef.current = false;
      }
  }
  

  return (
    <div>
      {snackbarData && (
        <SnackbarMessage
          message={snackbarData.message}
          type={snackbarData.type}
          open={snackbarOpen}
          handleClose={closeSnackbar}
        />
      )}
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        onClose={handleClose}
        PaperComponent={PaperComponent}
        aria-labelledby="withdraw-dialog-title"
      >
        <DialogTitle sx={{ cursor: 'move', borderBottom: '1px solid', borderColor: 'divider' }} id="withdraw-dialog-title">
          Ertu viss um að þú viljir afturkalla tilboðið?
        </DialogTitle>
        <DialogContent sx={{ py: 3, '&.MuiDialogContent-root': { pt: 3 } }} aria-busy={isWithdrawing}>
          {isWithdrawing && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000,
              pointerEvents: 'none',
            }}>
              <CircularProgress />
            </div>
          )}
          <OfferSummary offer={offer} tonQuotaTypes={tonQuotaTypes} />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" onClick={handleClose} disabled={isWithdrawing}>Loka</Button>
          <Button variant="contained" color="error" onClick={handleWithdraw} disabled={isWithdrawing}>Afturkalla</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
